import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, GlobalStyles, IconButton } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

const noop = () => {};
const ToastContext = createContext(noop);

const DURATION = 3500;

const CONFIG = {
  success: { Icon: CheckCircleOutlineIcon, color: '#10b981', glow: 'rgba(16,185,129,0.18)',  bg: 'rgba(16,185,129,0.1)', label: 'Success' },
  error:   { Icon: ErrorOutlineIcon,       color: '#ef4444', glow: 'rgba(239,68,68,0.18)',   bg: 'rgba(239,68,68,0.1)',  label: 'Error'   },
  warning: { Icon: WarningAmberIcon,       color: '#f59e0b', glow: 'rgba(245,158,11,0.18)',  bg: 'rgba(245,158,11,0.1)', label: 'Warning' },
  info:    { Icon: InfoOutlinedIcon,       color: '#6366f1', glow: 'rgba(99,102,241,0.18)',  bg: 'rgba(99,102,241,0.1)', label: 'Info'    },
};

function ToastItem({ id, message, severity, onRemove }) {
  const cfg = CONFIG[severity] || CONFIG.info;
  const { Icon } = cfg;
  const [phase, setPhase] = useState('entering');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('visible'),             20);
    const t2 = setTimeout(() => setPhase('leaving'), DURATION - 240);
    const t3 = setTimeout(() => onRemove(id),        DURATION);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [id, onRemove]);

  const handleClose = useCallback(() => {
    setPhase('leaving');
    setTimeout(() => onRemove(id), 240);
  }, [id, onRemove]);

  const entering = phase === 'entering';
  const leaving  = phase === 'leaving';

  return (
    <Box sx={{
      width: { xs: '100%', sm: 360 },
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '16px',
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(226,232,240,0.8)',
      boxShadow: [
        '0 0 0 1px rgba(255,255,255,0.9)',
        '0 8px 32px rgba(0,0,0,0.1)',
        '0 2px 8px rgba(0,0,0,0.06)',
        `0 0 24px ${cfg.glow}`,
      ].join(', '),
      transform: leaving  ? 'translateX(18px)' :
                 entering ? 'translateX(108%)'  : 'translateX(0)',
      opacity: leaving || entering ? 0 : 1,
      transition: leaving
        ? 'transform 0.24s ease-in, opacity 0.24s ease-in'
        : 'transform 0.46s cubic-bezier(0.34,1.56,0.64,1), opacity 0.32s ease',
    }}>

      {/* Countdown bar */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
        background: `linear-gradient(90deg, ${cfg.color} 0%, ${cfg.color}55 75%, transparent 100%)`,
        transformOrigin: 'left',
        animation: `toastProgress ${DURATION}ms linear forwards`,
      }} />

      {/* Sub-glow line */}
      <Box sx={{
        position: 'absolute', top: '3px', left: 0, right: 0, height: '1px',
        background: `linear-gradient(90deg, transparent 0%, ${cfg.color}1a 50%, transparent 100%)`,
      }} />

      {/* Content row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: { xs: '8px 10px', sm: '10px 12px' } }}>

        {/* Icon badge */}
        <Box sx={{
          flexShrink: 0,
          width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 },
          borderRadius: '50%',
          bgcolor: cfg.bg,
          border: `1px solid ${cfg.color}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 12px ${cfg.glow}, inset 0 1px 0 rgba(255,255,255,0.07)`,
          animation: 'toastIconPop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.12s both',
        }}>
          <Icon sx={{ fontSize: '1rem', color: cfg.color }} />
        </Box>

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: '0.825rem',
            color: '#1e293b', lineHeight: 1.2, mb: '2px',
            letterSpacing: '-0.01em',
          }}>
            {cfg.label}
          </Typography>
          <Typography sx={{
            fontSize: '0.75rem', color: '#64748b',
            lineHeight: 1.45, wordBreak: 'break-word',
          }}>
            {message}
          </Typography>
        </Box>

        {/* Close */}
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            flexShrink: 0, alignSelf: 'flex-start', mt: '-1px',
            color: '#94a3b8', width: 22, height: 22,
            transition: 'all 0.15s',
            '&:hover': { bgcolor: 'rgba(15,23,42,0.06)', color: '#475569' },
          }}
        >
          <CloseIcon sx={{ fontSize: '0.78rem' }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = useCallback((msg, sev = 'success') => {
    const id = ++idRef.current;
    setToasts(prev => {
      const next = [...prev, { id, message: msg, severity: sev }];
      return next.length > 3 ? next.slice(-3) : next;
    });
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}

      <GlobalStyles styles={{
        '@keyframes toastProgress': {
          from: { transform: 'scaleX(1)' },
          to:   { transform: 'scaleX(0)' },
        },
        '@keyframes toastIconPop': {
          '0%':   { transform: 'scale(0.35)', opacity: 0 },
          '65%':  { transform: 'scale(1.2)'              },
          '100%': { transform: 'scale(1)',    opacity: 1  },
        },
      }} />

      <Box sx={{
        position: 'fixed',
        top: { xs: 12, sm: 20 },
        right: { xs: 12, sm: 20 },
        left: { xs: 12, sm: 'auto' },
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}>
        {toasts.map(t => (
          <ToastItem key={t.id} id={t.id} message={t.message} severity={t.severity} onRemove={removeToast} />
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
